import { useState, useEffect, useCallback } from "react";
import { MoveLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dropdown, Button, Carousel, Modal } from "react-bootstrap";
import { sanitizeInput } from "../common/security-utils";
import { getTripsViewApi, getActualWorkflowApi, getEstimateWorkflowApi, deleteTripsApi, getStopsListApi, getAllocationHistoryApi, getStopProductListApi, getSkippedMilestoneApi, updateDriverAPI, getProductDetailsApi, productConfirmationApi, paymentUpdateApi, paymentUpdateImageApi, getUpdateMilestoneApi, getNextMilestoneApi, getUpdateSkippedMilestoneApi } from "./trips-api";
import { getTripPlanRouteApi } from "../live-view/live-view-api";
import moreIcon from "../../assets/svg/more_ic.svg";

import DeleteModal from "../common/deleteModel";
import activeStop from "../../assets/svg/active-location.svg";
import locationIcon from "../../assets/svg/location_ic.svg";
import activeWorkflow from "../../assets/svg/active-workflow.svg";
import workflowIcon from "../../assets/svg/workflow.svg";
import tripIcon from "../../assets/svg/trip_view_ic.svg";
import activeHistory from "../../assets/svg/history_active_ic.svg";
import historyIcon from "../../assets/svg/history_ic.svg";

import DriverAllocationModal from "./DriverAllocationModal";
import MilestoneUpdateModal from "./MilestoneUpdateModal";
import DeliveredModal from "./DeliveredModal";
import PaymentModal from "./PaymentModal";
import TripDetailsTab from "./TripDetailsTab";
import WorkflowTab from "./WorkflowTab";
import AllocationHistoryTab from "./AllocationHistoryTab";
import { useGlobal } from "../../GlobalContext";

import polyline from "@mapbox/polyline";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../common/skeleton-effect.css";
import CommonSnackbar from "../common/reusable-commponent";
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export const TripsView = () => {
  const { rolePermissionList, userType } = useGlobal();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state || {};
  const [viewStopDetails, setViewStopDetails] = useState({});

  // --- Snackbar State (unified) ---
  const [snackbar, setSnackbar] = useState({ show: false, message: "", status: false });
  const handleSnackbarClose = useCallback(() => setSnackbar((prev) => ({ ...prev, show: false })), []);
  const triggerSnackbar = useCallback((message, status) => {
    setSnackbar({ show: true, message: sanitizeInput(message), status });
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [isWorkflowLoading, setIsWorkflowLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState("trips");
  const [actualWorkflow, setActualWorkflow] = useState([]);
  const [estimateWorkflow, setEstimateWorkflow] = useState([]);
  const [allocationHistory, setAllocationHistory] = useState([]);
  const [stopsList, setStopsList] = useState([]);
  const [driverList, setDriverList] = useState([]);
  const [coDriverList, setCoDriverList] = useState([]);
  const [deliveryPersonList, setDeliveryPersonList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [selectedStopIndex, setSelectedStopIndex] = useState(-1);
  const [isProductLoading, setIsProductLoading] = useState(false);

  const [showMap, setShowMap] = useState(false);
  const [mapPosition, setMapPosition] = useState(null);

  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [attachmentUrls, setAttachmentUrls] = useState([]);

  const [stopPinLocation, setStopPinLocation] = useState([]);
  const [stopPolyCoords, setStopPolyCoords] = useState([]);
  const [polylinePlanned, setPolylinePlanned] = useState([]);
  const [startLocation, setStartLocation] = useState(null);
  const [selectedStop, setSelectedStop] = useState(-1);

  useEffect(() => {
    if (id != null) {
      getTripsView(id);
      getStopsList(id);
      getActualWorkflow(id);
      getEstimateWorkflow(id);
      getAllocationHistory(id);
      getTripPlanRoute(id);
    }
  }, [id]);

  function checkPermission(permission) {
    return rolePermissionList.findIndex((element) => element["name"] === "Trips" && element["menu_permission"] === true && element[permission] === true) > -1 ? true : false;
  }

  useEffect(() => {
    if (selectedStopIndex !== -1) {
      getProductList(stopsList[selectedStopIndex].stop_id);
    }
  }, [selectedStopIndex]);

  useEffect(() => {
    if (mapPosition != null) {
      setShowMap(true);
    }
  }, [mapPosition]);

  const getTripPlanRoute = useCallback(async (tripId) => {
    try {
      const response = await getTripPlanRouteApi(tripId);
      if (response.data.success === true) {
        const routeList = response.data.response ?? [];
        if (routeList.length > 0) {
          createPolylines(routeList);
        }
      }
    } catch (err) {
      // silent — view-only fetch
    }
  }, []);

  const createPolylines = useCallback((routeList) => {
    const newPolylines = [];

    routeList.forEach((item, index) => {
      if (item.points && typeof item.points === "string" && item.points.length > 0) {
        try {
          const decoded = polyline.decode(item.points);

          if (decoded && decoded.length > 0) {
            if (index === 0) {
              setStartLocation({
                lat: decoded[0][0],
                lng: decoded[0][1],
              });
            }

            newPolylines.push({
              id: `poly_planned_${index}`,
              positionList: decoded,
            });
          }
        } catch (parseError) {
          // skip invalid polyline
        }
      }
    });

    setPolylinePlanned(newPolylines);
  }, []);

  const getTripsView = useCallback(async (tripId) => {
    setIsLoading(true);
    try {
      const response = await getTripsViewApi(tripId);
      if (response.data.success === true) {
        setViewStopDetails(response.data.trip ?? {});
        setDriverList(response.data.driver ?? []);
        setCoDriverList(response.data.co_driver ?? []);
        setDeliveryPersonList(response.data.delivery_person ?? []);
      }
    } catch (err) {
      // silent
    } finally {
      setIsLoading(false);
    }
  }, []);
  const getStopsList = useCallback(async (tripId) => {
    try {
      const response = await getStopsListApi(tripId);
      if (response.data.success === true) {
        setStopsList(response.data.stops ?? []);
        setStopPinLocation(response.data.stops?.filter((marker) => marker["to_latitude"] != null && marker["to_longitude"] != null && marker["to_latitude"] !== "" && marker["to_longitude"] !== ""));

        const polylineList = (response.data.stops ?? []).map((coords) => [coords.to_latitude, coords.to_longitude].map(Number));
        setStopPolyCoords(polylineList);
      }
    } catch (err) {
      // silent
    }
  }, []);
  const getActualWorkflow = useCallback(async (tripId) => {
    setIsWorkflowLoading(true);
    try {
      const response = await getActualWorkflowApi(tripId);
      if (response.data.success === true) {
        setActualWorkflow(response.data.trip_history ?? []);
      }
    } catch (err) {
      // silent
    } finally {
      setIsWorkflowLoading(false);
    }
  }, []);

  const getEstimateWorkflow = useCallback(async (tripId) => {
    setIsWorkflowLoading(true);
    try {
      const response = await getEstimateWorkflowApi(tripId);
      if (response.data.success === true) {
        setEstimateWorkflow(response.data.est_workflow ?? []);
      }
    } catch (err) {
      // silent
    } finally {
      setIsWorkflowLoading(false);
    }
  }, []);

  const getAllocationHistory = useCallback(async (tripId) => {
    setIsHistoryLoading(true);
    try {
      const response = await getAllocationHistoryApi(tripId);
      if (response.data.success === true) {
        setAllocationHistory(response.data.history ?? []);
      }
    } catch (err) {
      // silent
    } finally {
      setIsHistoryLoading(false);
    }
  }, []);
  const getProductList = useCallback(async (stopId) => {
    setIsProductLoading(true);
    try {
      const response = await getStopProductListApi(stopId);
      if (response.data.success === true) {
        setProductList(response.data.products ?? []);
      }
    } catch (err) {
      // silent
    } finally {
      setIsProductLoading(false);
    }
  }, []);

  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  useEffect(() => {
    if (deleteId !== null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  const deleteTrip = useCallback(async () => {
    try {
      const response = await deleteTripsApi(deleteId);
      if (response.data.success === true) {
        navigate(-1);
      }
    } catch (err) {
      // silent
    }
  }, [deleteId, navigate]);




  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [isSkippedMilestone, setIsSkippedMilestone] = useState(false);
  const [milestoneDetails, setMilestoneDetails] = useState({});

  useEffect(() => {
    if (isSkippedMilestone === true) {
      setShowMilestoneModal(true);
    }
  }, [isSkippedMilestone]);

  const getMilestoneDetails = useCallback(async (tripId) => {
    setIsSkippedMilestone(false);
    try {
      const response = await getNextMilestoneApi(tripId);
      if (response.data.success === true) {
        setMilestoneDetails(response.data);
        setShowMilestoneModal(true);
      }
    } catch (err) {
      // silent
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getSkippedMilestoneDetails = useCallback(async (tripId, stopId) => {
    try {
      const response = await getSkippedMilestoneApi(tripId, stopId);
      if (response.data.success === true) {
        setMilestoneDetails(response.data);
        setIsSkippedMilestone(true);
      }
    } catch (err) {
      // silent
    } finally {
      setIsLoading(false);
    }
  }, []);

  const INITIAL_MILESTONE_ERRORS = { status: "", comments: "", signature: "", attachments: "", latitude: "", longitude: "" };
  const INITIAL_PAYMENT_ERRORS = { payment_method: "", amount: "", signature: "", attachments: "" };
  const INITIAL_ALLOCATION_ERRORS = { vehicle_id: "", users: "" };

  const [errorMsgMilestone, setErrorMsgMilestone] = useState({ ...INITIAL_MILESTONE_ERRORS });
  const [errorMsgAllocation, setErrorMsgAllocation] = useState({ ...INITIAL_ALLOCATION_ERRORS });
  const [errorMsgPaymentUpdate, setErrorMsgPaymentUpdate] = useState({ ...INITIAL_PAYMENT_ERRORS });

  /** Parse validation errors from API response into a state object */
  const parseValidationErrors = useCallback((errors, template) => {
    if (!errors || typeof errors !== "object") return template;
    const result = { ...template };
    Object.entries(errors).forEach(([key, value]) => {
      const rawMsg = Array.isArray(value) && value.length > 0 ? value[0] : "";
      result[key] = sanitizeInput(rawMsg);
    });
    return result;
  }, []);

  const handleMilestoneUpdate = useCallback(async (payload) => {
    try {
      let response = null;
      if (isSkippedMilestone === false) {
        response = await getUpdateMilestoneApi(payload);
      } else {
        payload.trip_id = id;
        payload.stop_id = selectedStop;
        response = await getUpdateSkippedMilestoneApi(id, payload);
      }
      if (response.status === 400) {
        setErrorMsgMilestone(parseValidationErrors(response.data.error, INITIAL_MILESTONE_ERRORS));
      } else if (response.data.success === true) {
        setIsSkippedMilestone(false);
        setShowMilestoneModal(false);
        if (response.data?.redirectPage === "production-confirmation") {
          await getProductDetails(payload.stop_id);
        } else {
          setMilestoneDetails({});
          getTripsView(id);
          getActualWorkflow(id);
        }
      } else {
        setErrorMsgMilestone(parseValidationErrors(response.data.error, INITIAL_MILESTONE_ERRORS));
      }
    } catch (err) {
      triggerSnackbar(err.response?.data?.message || "An error occurred while updating the milestone", false);
    } finally {
      setIsLoading(false);
    }
  }, [isSkippedMilestone, id, selectedStop, parseValidationErrors, getTripsView, getActualWorkflow, triggerSnackbar]);

  const [showDeliveredModal, setShowDeliveredModal] = useState(false);
  const [stopProduct, setStopProduct] = useState([]);
  const getProductDetails = useCallback(async (stopId) => {
    try {
      const response = await getProductDetailsApi(stopId);
      if (response.data.success === true) {
        setStopProduct(
          response.data.products?.map((product) => ({
            ...product,
            duplicateprice: product.price,
            singleprice: (parseFloat(product.price) || 0) / (parseInt(product.quantity) || 1),
            returnqty: 0,
            dueqty: 0,
          }))
        );
        setShowDeliveredModal(true);
      }
    } catch (err) {
      // silent
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleDeliveryUpdate = useCallback(async (payload) => {
    try {
      payload.stop_id = milestoneDetails.stopId ?? 0;
      payload.trip_id = milestoneDetails?.trip?.id ?? 0;
      payload.milestone_id = milestoneDetails?.status.length > 0 ? milestoneDetails?.status[0].milestone_id : 0;

      if (payload.stop_id === 0 || payload.trip_id === 0 || payload.milestone_id === 0) {
        triggerSnackbar("Missing required information. Please try updating the milestone first.", false);
        return;
      }

      const response = await productConfirmationApi(payload);

      if (response.data.success === true) {
        if (response.data.redirectPage === "") {
          setShowDeliveredModal(false);
          getTripsView(id);
          getActualWorkflow(id);
        } else if (response.data.redirectPage === "payment") {
          setShowDeliveredModal(false);
          setShowPaymentModal(true);
        }
      } else {
        triggerSnackbar(response.data.message || "Failed to update delivery information", false);
      }
    } catch (err) {
      triggerSnackbar(err.response?.data?.message || "An error occurred while updating delivery information", false);
    } finally {
      setIsLoading(false);
    }
  }, [milestoneDetails, id, triggerSnackbar, getTripsView, getActualWorkflow]);
  const handlePaymentUpdate = useCallback(async (payload) => {
    try {
      payload.stop_id = milestoneDetails.stopId ?? 0;
      payload.trip_id = milestoneDetails?.trip?.id ?? 0;
      payload.milestone_id = milestoneDetails?.status.length > 0 ? milestoneDetails?.status[0].milestone_id : 0;
      const response = await paymentUpdateApi(payload);
      if (response.status === 400) {
        setErrorMsgPaymentUpdate(parseValidationErrors(response.data.error, INITIAL_PAYMENT_ERRORS));
      } else if (response.data.success === true) {
        if (response.data.id && (payload.signature || (payload.attachments && payload.attachments.length > 0))) {
          await paymentUpdateImageApi({
            id: response.data.id,
            billing_proof: (payload.attachments && payload.attachments[0]) || "",
            signature: payload.signature || "",
          });
        }
        setShowPaymentModal(false);
        triggerSnackbar(response.data.message, true);
        getTripsView(id);
        getActualWorkflow(id);
      } else {
        setErrorMsgPaymentUpdate(parseValidationErrors(response.data.error, INITIAL_PAYMENT_ERRORS));
      }
    } catch (err) {
      // silent
    } finally {
      setIsLoading(false);
    }
  }, [milestoneDetails, id, triggerSnackbar, parseValidationErrors, getTripsView, getActualWorkflow]);

  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [showDriverAllocationModal, setShowDriverAllocationModal] = useState(false);
  const handleDriverAllocate = useCallback(async (payload) => {
    try {
      const response = await updateDriverAPI(payload);
      if (response.status === 400) {
        setErrorMsgAllocation(parseValidationErrors(response.data.error, INITIAL_ALLOCATION_ERRORS));
      } else if (response.data.success === true) {
        setShowDriverAllocationModal(false);
        triggerSnackbar(response.data.message || "Driver assigned successfully", true);
        getTripsView(id);
        getAllocationHistory(id);
      } else {
        setErrorMsgAllocation(parseValidationErrors(response.data.error, INITIAL_ALLOCATION_ERRORS));
      }
    } catch (err) {
      // silent
    } finally {
      setIsLoading(false);
    }
  }, [id, triggerSnackbar, parseValidationErrors, getTripsView, getAllocationHistory]);

  return (
    <>
      <DriverAllocationModal show={showDriverAllocationModal} onHide={() => setShowDriverAllocationModal(false)} tripId={id} onSubmit={handleDriverAllocate} errorMsgAllocation={errorMsgAllocation} />
      <MilestoneUpdateModal
        show={showMilestoneModal}
        onHide={() => {
          setMilestoneDetails({});
          setIsSkippedMilestone(false);
          setShowMilestoneModal(false);
        }}
        onSubmit={handleMilestoneUpdate}
        milestoneDetails={milestoneDetails}
        errorMsgMilestone={errorMsgMilestone}
        skippedUpdate={isSkippedMilestone}
      />
      <DeliveredModal
        show={showDeliveredModal}
        onHide={() => {
          setShowDeliveredModal(false);
        }}
        onSubmit={handleDeliveryUpdate}
        products={stopProduct}
      />
      <PaymentModal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} onSubmit={handlePaymentUpdate} errorMsgPaymentUpdate={errorMsgPaymentUpdate} />
      <DeleteModal
        title={"trips"}
        isOpen={isOpenDelete}
        onClose={() => {
          setIsOpenDelete(false); // close modal
          setDeleteId(null); // reset selected ID
        }}
        onDelete={deleteTrip}
      />
      <div>
        <div
          className="mt-2 py-2 bg-white d-flex align-items-center justify-content-between"
          style={{
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
          }}
        >
          {isLoading ? (
            // Skeleton for header
            <div className="d-flex align-items-center justify-content-start">
              <div className="ms-2 skeleton" style={{ width: "36px", height: "36px", borderRadius: "50%" }} />
              <div className="ms-3 skeleton skeleton-avatar" />
              <div className="ms-2 d-flex flex-column align-items-start justify-content-center gap-2" style={{ flex: 1 }}>
                <div className="skeleton skeleton-text" style={{ width: "200px", height: "20px" }} />
                <div className="skeleton skeleton-text-sm" style={{ width: "300px" }} />
              </div>
            </div>
          ) : (
            <div className="d-flex align-items-center justify-content-start">
              <div
                className="ms-2 d-flex align-items-center justify-content-center  rounded-5 bg-white"
                style={{
                  width: "36px",
                  height: "36px",
                  border: "1px solid #EBEBEB",
                }}
              >
                <MoveLeft
                  className="cursor-pointer"
                  onClick={() => {
                    navigate(-1);
                  }}
                />
              </div>

              <div
                className="ms-3 d-flex align-items-center justify-content-center"
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "16px",
                  background: "#DFE8FF",
                  color: "#3469E7",
                }}
              >
                <img src={tripIcon} alt="tripIcon" />
              </div>
              <div className="ms-2 d-flex flex-column align-items-start justify-content-center gap-1">
                <div className="fs-20 fw-600 textFontColor">{viewStopDetails.trip_id ?? ""}</div>
                <div className="d-flex align-items-center justify-content-start">
                  <img src={locationIcon} alt="location" />
                  <span className="ms-2 fs-13 fw-400 " style={{ color: "#A2A7AE" }}>
                    {viewStopDetails.location ?? ""}
                  </span>
                  <div
                    className="mx-2"
                    style={{
                      width: "5px",
                      height: "5px",
                      borderRadius: "100px",
                      background: "#D9D9D9",
                    }}
                  ></div>
                  <div className="d-flex align-items-center justify-content-center rounded-4 px-2 py-1" style={{ background: "#EDF2FF", color: "#3469E7" }}>
                    <span className="fs-12 fw-500">#{viewStopDetails.customer_code ?? ""}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="d-flex align-items-center justify-content-start">
            {isLoading ? (
              <>
                <div className="skeleton skeleton-button" style={{ width: "120px", height: "40px", borderRadius: "100px" }} />
                <div className="ms-2 skeleton" style={{ width: "36px", height: "36px", borderRadius: "50%" }} />
              </>
            ) : (
              <>
                <Dropdown>
                  <Dropdown.Toggle
                    bsPrefix="custom-toggle"
                    variant="light"
                    id="dropdown-sort"
                    className="d-flex align-items-center justify-content-between backgroundLightOrange rounded-5"
                    style={{
                      padding: "10px 12px",
                    }}
                  >
                    <span className="d-flex align-items-center px-1 gap-2">
                      <span
                        className="backgroundYellow"
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "3px",
                          display: "inline-block",
                        }}
                      ></span>

                      <span className="dropdown-text textcolor fs-13 fw-500">{viewStopDetails.status ?? "-"}</span>

                      {/* <span className="dropdown-toggle-arrow"></span> */}
                    </span>
                  </Dropdown.Toggle>
                </Dropdown>

                <div className="ms-2">
                  {(((checkPermission("edit_permission") || checkPermission("delete_permission")) && viewStopDetails.status === "Created") || viewStopDetails.status !== "Trip end") && (
                    <Dropdown>
                      <Dropdown.Toggle
                        bsPrefix="custom-toggle"
                        variant="light"
                        id="dropdown-sort"
                        className="d-flex align-items-center justify-content-between  rounded-5 bg-white"
                        style={{
                          width: "36px",
                          height: "36px",
                          border: "1px solid #EBEBEB",
                        }}
                      >
                        <span className="d-flex align-items-center px-1 gap-2">
                          <img src={moreIcon} alt="more" />
                        </span>
                      </Dropdown.Toggle>

                      <Dropdown.Menu
                        style={{
                          backgroundColor: "white",
                          borderRadius: "8px",
                          border: "1px solid #EBEBEB",
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        {viewStopDetails.status !== "Trip end" && (
                          <Dropdown.Item
                            className="fs-14 fw-500 textcolor"
                            onClick={() => {
                              getMilestoneDetails(id);
                            }}
                          >
                            Update Milestone
                          </Dropdown.Item>
                        )}

                        {viewStopDetails.status !== "Trip end" && (
                          <Dropdown.Item
                            className="fs-14 fw-500 textcolor"
                            onClick={() => {
                              setShowDriverAllocationModal(true);
                            }}
                          >
                            Allocate Driver
                          </Dropdown.Item>
                        )}

                        {checkPermission("edit_permission") && viewStopDetails.status === "Created" && (
                          <Dropdown.Item
                            className="fs-14 fw-500 textcolor"
                            onClick={() => {
                              navigate("/trips/create", {
                                state: { id: id },
                              });
                            }}
                          >
                            Edit
                          </Dropdown.Item>
                        )}
                        {checkPermission("delete_permission") && viewStopDetails.status === "Created" && (
                          <Dropdown.Item
                            className="fs-14 fw-500 textcolor"
                            onClick={() => {
                              setDeleteId(id);
                            }}
                          >
                            Delete
                          </Dropdown.Item>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="w-100 mb-1  d-flex align-items-center justify-content-start" style={{ background: "#EBF3FF" }}>
          <div
            className={`cursor-pointer d-flex align-items-center justify-content-center  px-2 fs-13  ${selectedTab === "trips" ? "fw-500 active-bottom-bar " : " fw-400"} `}
            style={{
              height: "40px",
              minWidth: "110px",
              color: selectedTab === "trips" ? "#3469E7" : "#5F5E5B",
            }}
            onClick={() => {
              setSelectedTab("trips");
            }}
          >
            <img src={selectedTab === "trips" ? activeStop : locationIcon} alt="tab" className="me-1" /> Trip details
          </div>
          <div
            className={`cursor-pointer d-flex align-items-center justify-content-center  px-2 fs-13  ${selectedTab === "workflow" ? "fw-500 active-bottom-bar " : " fw-400"} `}
            style={{
              height: "40px",
              minWidth: "110px",
              color: selectedTab === "workflow" ? "#3469E7" : "#5F5E5B",
            }}
            onClick={() => {
              setSelectedTab("workflow");
            }}
          >
            <img src={selectedTab === "workflow" ? activeWorkflow : workflowIcon} alt="tab" className="me-1" /> Workflow
          </div>

          <div
            className={`cursor-pointer d-flex align-items-center justify-content-center  px-2 fs-13  ${selectedTab === "history" ? "fw-500 active-bottom-bar " : " fw-400"} `}
            style={{
              height: "40px",
              minWidth: "110px",
              color: selectedTab === "history" ? "#3469E7" : "#5F5E5B",
            }}
            onClick={() => {
              setSelectedTab("history");
            }}
          >
            <img src={selectedTab === "history" ? activeHistory : historyIcon} alt="tab" className="me-1" /> Allocation history
          </div>
        </div>
        {selectedTab === "trips" && (
          <TripDetailsTab
            isLoading={isLoading}
            viewStopDetails={viewStopDetails}
            driverList={driverList}
            coDriverList={coDriverList}
            deliveryPersonList={deliveryPersonList}
            stopsList={stopsList}
            productList={productList}
            isProductLoading={isProductLoading}
            stopPinLocation={stopPinLocation}
            polylinePlanned={polylinePlanned}
            startLocation={startLocation}
            selectedStopIndex={selectedStopIndex}
            setSelectedStopIndex={setSelectedStopIndex}
            setMapPosition={setMapPosition}
          />
        )}
        {selectedTab === "workflow" && (
          <WorkflowTab
            isWorkflowLoading={isWorkflowLoading}
            estimateWorkflow={estimateWorkflow}
            actualWorkflow={actualWorkflow}
            setSelectedStop={setSelectedStop}
            getSkippedMilestoneDetails={getSkippedMilestoneDetails}
            setAttachmentUrls={setAttachmentUrls}
            setShowAttachmentModal={setShowAttachmentModal}
          />
        )}
        {selectedTab === "history" && (
          <AllocationHistoryTab
            isHistoryLoading={isHistoryLoading}
            allocationHistory={allocationHistory}
          />
        )}
        <Modal show={showMap} onHide={() => setShowMap(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>Stop Location</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ height: "400px", width: "100%" }}>
              <MapContainer center={[20.5937, 78.9629]} zoom={5} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
                {mapPosition && <Marker position={mapPosition} />}
              </MapContainer>
            </div>
            <div className="mt-2 text-muted fs-13">{mapPosition ? `Selected: ${mapPosition.lat}, ${mapPosition.lng}` : "Click on the map to select a location"}</div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowMap(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
      <Modal show={showAttachmentModal} onHide={() => setShowAttachmentModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Attachment</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex justify-content-center align-items-center bg-light p-0">
          {attachmentUrls.length > 0 ? (
            <Carousel interval={null} className="w-100" style={{ background: "#333" }}>
              {attachmentUrls.map((url, index) => (
                <Carousel.Item key={index} className="text-center">
                  <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
                    <img
                      src={url}
                      alt={`Attachment ${index + 1}`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </Carousel.Item>
              ))}
            </Carousel>
          ) : attachmentUrl ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh", width: "100%" }}>
              <img
                src={attachmentUrl}
                alt="Attachment"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </div>
          ) : null}
        </Modal.Body>
      </Modal>
      <CommonSnackbar
        show={snackbar.show}
        message={snackbar.message}
        isSuccess={snackbar.status}
        onClose={handleSnackbarClose}
        position="top-end"
      />
    </>
  );
};
